/**
 * Formes des lignes du miroir, réduites à ce que la PWA affiche ou écrit.
 *
 * Ce ne sont pas les tables entières : on ne `select` que ces colonnes, pour
 * que le premier chargement (1 800 sociétés + 2 300 liens) reste léger sur
 * une 4G de salle d'attente.
 *
 * Les colonnes de contrôle du miroir (`updated_at`, `device_id`,
 * `deleted_at`, `server_seq`) ne figurent ici que là où l'UI en a besoin :
 * la couche d'écriture (`write.ts`) les pose elle-même.
 */

/** Un tier du vocabulaire `crm_settings.tiers`. */
export interface Tier {
  code: string; // "A".."F" — donne l'ordre, stable dans les segments
  label: string; // libellé utilisateur, éditable côté Mac
  cadence_days: number | null; // null = pas de radar de relance
}

export interface CrmVocab {
  /** `crm_settings.company_classifications` — multi-valué sur la société. */
  classifications: string[];
  /** `crm_settings.buyer_profiles` — une seule valeur par société. */
  buyerProfiles: string[];
  /** `crm_settings.contact_functions` — multi-valué, porté par le lien. */
  functions: string[];
  tiers: Tier[];
}

export interface CrmTag {
  uid: string;
  name: string;
  color: string | null;
  description: string | null;
}

export interface Company {
  uid: string;
  name: string;
  domain: string | null;
  city: string | null;
  country_code: string | null;
  website: string | null;
  naf_label: string | null;
  revenue: number | null;
  employees: number | null;
  notes: string | null;
  archived: boolean;
  created_at: string;
  /** Tableau jsonb, ex. `["customer","supplier"]`. */
  classification: string[] | null;
  buyer_profile: string | null;
  tier: string | null;
  /**
   * Classée à la main : la fiche quitte les files même si des cases restent
   * vides. C'est l'échappatoire aux cas que la règle de complétude ne peut
   * pas couvrir (un partenaire sans étiquette, une administration sans
   * cadence de relance).
   */
  reviewed_at: string | null;
  /** Override de la cadence du tier. NULL = cadence du tier. */
  cadence_days: number | null;
}

export interface Contact {
  uid: string;
  email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  /** Titre libre extrait des signatures — une preuve, pas un vocabulaire. */
  role: string | null;
  phone: string | null;
}

/**
 * Une arête de l'organigramme. `functions` vit ici et non sur le contact :
 * « acheteur » veut dire « acheteur CHEZ cette société » (cf. docs/CRM2.md
 * §1.3). Le même contact peut être `management` ailleurs.
 */
export interface Link {
  contact_uid: string;
  company_uid: string;
  is_primary: boolean;
  active: boolean;
  role: string | null;
  functions: string[] | null;
  created_at: string;
  contact: Contact | null;
}

/** Un lien étiquette ↔ société. Polymorphe côté base, ici toujours company. */
export interface TagLink {
  tag_uid: string;
  entity_uid: string;
  created_at: string;
}

/** Libellés FR des vocabulaires fermés. Une valeur inconnue s'affiche brute. */
export const CLASSIFICATION_LABELS: Record<string, string> = {
  customer: "Client",
  supplier: "Fournisseur",
  intermediary: "Négociant",
  logistics: "Transport",
  administration: "Administration",
  service_provider: "Prestataire",
  partner: "Partenaire",
  noise: "Bruit",
  other: "Autre",
};

export const BUYER_PROFILE_LABELS: Record<string, string> = {
  trader: "Trader",
  stockist: "Stockeur",
  on_demand: "À la demande",
  retail: "Détaillant",
};

export const FUNCTION_LABELS: Record<string, string> = {
  buyer: "Acheteur",
  seller: "Vendeur",
  accounting: "Compta",
  logistics: "Transport",
  backoffice: "Back-office",
  management: "Direction",
  other: "Autre",
};

export function label(dict: Record<string, string>, value: string): string {
  return dict[value] ?? value;
}

/** Nom lisible d'un contact, avec repli en cascade jusqu'à l'email. */
export function contactName(c: Contact): string {
  const full = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return c.display_name?.trim() || full || c.email;
}
